import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Grid } from '@mui/material';
import { useState } from 'react';
import { format, parseISO, isValid } from 'date-fns';
import FieldHelp from '../../components/FieldHelp';

type Props = {
    grid?: boolean;
    caption?: string;
    /** Texto de ayuda: si se pasa, se muestra un icono de ayuda junto al campo. */
    help?: string;
    onChange?: (value: { name: string | undefined; value: unknown }) => void;
    defVal?: string | null;
    name?: string;
    disabled?: boolean;
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
    [key: string]: unknown;
};

/**
 * Form Date
 *
 * Props
 *
 *     * string defVal: cadena en formato ISO (2010-01-01T10:20:20).
 *     * function onClick({name, value}): el valor es en formato ISO.
 */
export function SttFormDate({
    grid = false,
    caption,
    help,
    onChange,
    defVal,
    name,
    disabled,
    xs,
    sm,
    md,
    lg,
    xl,
    ...rest
}: Props): React.ReactElement {
    const [open, setOpen] = useState<boolean>(false);

    const _onChange = (date: Date | null) => {
        if (date && isValid(date)) {
            onChange?.({
                name,
                value: format(date, "yyyy-MM-dd'T'HH:mm:ss"),
            });
            setOpen(false);
        } else {
            onChange?.({ name, value: null });
        }
    };

    // Parse defVal to Date object
    const parsedValue = (() => {
        if (!defVal) return null;
        try {
            const parsed = parseISO(defVal);
            return isValid(parsed) ? parsed : null;
        } catch {
            return null;
        }
    })();

    const picker = (
        <DatePicker
            slotProps={{
                textField: {
                    variant: 'outlined',
                    size: 'small',
                    style: { width: '100%' },
                    onClick: () => !disabled && setOpen(true),
                },
                openPickerButton: {
                    onClick: () => !disabled && setOpen(true),
                },
                actionBar: {
                    actions: ['clear', 'cancel', 'accept'],
                },
            }}
            label={caption}
            value={parsedValue}
            onChange={_onChange}
            format="dd/MM/yyyy"
            disabled={disabled}
            open={open}
            onClose={() => setOpen(false)}
            onOpen={() => !disabled && setOpen(true)}
        />
    );

    const content = help ? (
        <div className="flex items-center gap-1">
            <div className="min-w-0 flex-1">{picker}</div>
            <FieldHelp text={help} />
        </div>
    ) : (
        picker
    );

    // Wrapped
    const sizeProps = { xs, sm, md, lg, xl };
    return grid ? (
        <Grid size={sizeProps} {...rest}>
            {content}
        </Grid>
    ) : (
        <>{content}</>
    );
}
